// Copyright (c) 2026 Michael F. Kamprath
//
// settings-panel.js — slide-in settings drawer.
//
// Exposes a hamburger/gear button that toggles a panel. The panel
// contains the user-facing preferences backed by settings.js:
//
//   - Verbal descriptions (on/off)
//   - Language (English-only for Phase 3; selector is rendered but
//     disabled to signal "coming soon" without removing the wiring).
//   - Support block: optional PayPal donation form, hidden when
//     the app is offline (since the form posts to paypal.com).
//   - About block: app name, copyright, and the privacy notice
//     (we do not collect, store, or transmit anything).
//
// The panel is intentionally simple: no focus trap library, no
// external animation library. CSS handles the slide transition.

/**
 * PayPal "no-code payment" link for supporting I Hear Ewe development.
 * Provided by the PayPal dashboard — see the snippet Michael pasted
 * when the Phase 7 Support section was scoped. The full button is a
 * plain form POST with target="_blank", so there is no JavaScript
 * SDK, no tracking, no dependency on Apple Pay / Google Pay wallets.
 * Changing Michael's PayPal identifier is a one-line change here.
 */
const PAYPAL_FORM_ACTION = 'https://www.paypal.com/ncp/payment/XX668BWXUM9AY';
const PAYPAL_CARDS_IMG = 'https://www.paypalobjects.com/images/Debit_Credit_APM.svg';
const PAYPAL_WORDMARK_IMG =
  'https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg';

/**
 * Path where aggregate, anonymous tap counts are shared. Linked from
 * the Privacy section so parents can see exactly what data the app
 * reports. This is a relative path so it works in any deployment —
 * the reverse proxy routes it to the Flask telemetry server, just
 * like /api and /metrics.
 */
const ANALYTICS_URL = '/stats';

import { t } from './i18n.js';
import {
  getVerbalDescriptions,
  setVerbalDescriptions,
  getShareStats,
  setShareStats,
  getLocale,
  setLocale,
} from './settings.js';
import {
  getAvailableLocales,
  getLocaleDisplayName,
  setLocale as setI18nLocale,
} from './i18n.js';
import { getOtherFamilyApps } from './family-apps.js';

/**
 * @typedef {Object} SettingsPanelHandles
 * @property {HTMLElement} toggleButton - The gear button to put in the chrome.
 * @property {HTMLElement} panelElement - The slide-in panel itself.
 * @property {() => void} open
 * @property {() => void} close
 * @property {() => boolean} isOpen
 * @property {() => void} destroy
 */

/**
 * Build the settings panel. Caller is responsible for inserting
 * `toggleButton` and `panelElement` into the document.
 *
 * @returns {SettingsPanelHandles}
 */
export function createSettingsPanel() {
  // --- Toggle (gear) button -------------------------------------------------
  const toggleButton = document.createElement('button');
  toggleButton.type = 'button';
  toggleButton.className = 'settings-toggle';
  toggleButton.setAttribute('aria-label', t('settings.title'));
  toggleButton.setAttribute('aria-expanded', 'false');
  // Simple inline SVG gear so we don't need an icon font.
  toggleButton.innerHTML = `
    <svg class="settings-toggle__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M19.14 12.94a7.49 7.49 0 0 0 .05-.94 7.49 7.49 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.61-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54a7.03 7.03 0 0 0-1.62.94l-2.39-.96a.5.5 0 0 0-.61.22L2.65 8.84a.5.5 0 0 0 .12.64L4.8 11.06c-.03.31-.05.62-.05.94s.02.63.05.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.43.33.68.22l2.39-.96c.5.38 1.04.7 1.62.94l.36 2.54c.05.25.26.42.5.42h3.84c.25 0 .46-.17.5-.42l.36-2.54c.58-.24 1.12-.56 1.62-.94l2.39.96c.25.1.54.02.68-.22l1.92-3.32a.5.5 0 0 0-.12-.64ZM12 15.5A3.5 3.5 0 1 1 15.5 12 3.5 3.5 0 0 1 12 15.5Z"/>
    </svg>
  `;

  // --- Panel ----------------------------------------------------------------
  const panelElement = document.createElement('aside');
  panelElement.className = 'settings-panel';
  panelElement.setAttribute('role', 'dialog');
  panelElement.setAttribute('aria-modal', 'false');
  panelElement.setAttribute('aria-label', t('settings.title'));
  panelElement.hidden = true;

  const header = document.createElement('header');
  header.className = 'settings-panel__header';
  const headingEl = document.createElement('h2');
  headingEl.className = 'settings-panel__title';
  headingEl.textContent = t('settings.title');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'settings-panel__close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.textContent = '\u00d7'; // ×
  header.appendChild(headingEl);
  header.appendChild(closeBtn);
  panelElement.appendChild(header);

  const body = document.createElement('div');
  body.className = 'settings-panel__body';
  panelElement.appendChild(body);

  // --- Verbal descriptions row ---------------------------------------------
  const verbalRow = document.createElement('label');
  verbalRow.className = 'settings-row';

  const verbalLabel = document.createElement('span');
  verbalLabel.className = 'settings-row__label';
  verbalLabel.textContent = t('settings.verbalDescriptions');

  const verbalToggle = document.createElement('input');
  verbalToggle.type = 'checkbox';
  verbalToggle.className = 'settings-row__toggle';
  verbalToggle.checked = getVerbalDescriptions();
  verbalToggle.addEventListener('change', () => {
    setVerbalDescriptions(verbalToggle.checked);
  });

  verbalRow.appendChild(verbalLabel);
  verbalRow.appendChild(verbalToggle);
  body.appendChild(verbalRow);

  // --- Share anonymous usage stats row ---------------------------------------
  const statsRow = document.createElement('label');
  statsRow.className = 'settings-row';

  const statsLabel = document.createElement('span');
  statsLabel.className = 'settings-row__label';
  statsLabel.textContent = t('settings.shareStats');

  const statsToggle = document.createElement('input');
  statsToggle.type = 'checkbox';
  statsToggle.className = 'settings-row__toggle';
  statsToggle.checked = getShareStats();
  statsToggle.addEventListener('change', () => {
    setShareStats(statsToggle.checked);
  });

  statsRow.appendChild(statsLabel);
  statsRow.appendChild(statsToggle);
  body.appendChild(statsRow);

  // --- Language row ---------------------------------------------------------
  const langRow = document.createElement('label');
  langRow.className = 'settings-row';

  const langLabel = document.createElement('span');
  langLabel.className = 'settings-row__label';
  langLabel.textContent = t('settings.language');

  const langSelect = document.createElement('select');
  langSelect.className = 'settings-row__select';
  const locales = getAvailableLocales();
  for (const code of locales) {
    const opt = document.createElement('option');
    opt.value = code;
    // Show the language in its own script ("English", "Español", ...)
    // rather than the bare code. getLocaleDisplayName falls back to
    // the uppercased code if a display name hasn't been registered,
    // so unknown locales remain visible rather than blank.
    opt.textContent = getLocaleDisplayName(code);
    langSelect.appendChild(opt);
  }
  langSelect.value = getLocale();
  // Disable the selector when only one locale is registered.
  if (locales.length < 2) {
    langSelect.disabled = true;
  }
  langSelect.addEventListener('change', () => {
    const code = langSelect.value;
    // Persist to localStorage and sync the i18n runtime.
    setLocale(code);
    setI18nLocale(code);
    // Reload so every translated string and locale-specific intro
    // audio path picks up the new language in one shot.
    window.location.reload();
  });

  langRow.appendChild(langLabel);
  langRow.appendChild(langSelect);
  body.appendChild(langRow);

  // --- Full-screen row ------------------------------------------------------
  // Toddler-handed-a-tablet scenario: the parent doesn't want the
  // kid accidentally tapping the browser back button, the URL bar,
  // the tab switcher, or any other piece of chrome that takes the
  // child out of the app. Going fullscreen via the Fullscreen API
  // hides the entire chrome until either the parent toggles it back
  // off or the user/browser explicitly exits (Esc, swipe-down on
  // iPad, etc.).
  //
  // Feature detection notes:
  //   * `document.fullscreenEnabled` is the canonical signal that
  //     the API is BOTH present and currently allowed (some embeds
  //     and iframes have it disabled by policy).
  //   * Safari historically shipped this API behind a `webkit`
  //     prefix and only added the unprefixed names in 16.4 (March
  //     2023). Pre-16.4 desktop Safari, and iPad Safari versions
  //     released before then, ONLY have `webkitRequestFullscreen` /
  //     `webkitFullscreenEnabled` / `webkitfullscreenchange` /
  //     `webkitFullscreenElement`. Detecting only the unprefixed
  //     names (an earlier rev of this code did exactly that) makes
  //     the row vanish on those Safari builds even though the API
  //     itself is fully functional. We feature-detect both names
  //     and fall through to whichever one the runtime exposes.
  //   * iOS Safari on iPhone does NOT support requestFullscreen at
  //     all — neither the unprefixed nor the webkit-prefixed form
  //     is available on iPhone, in any iOS version. Hiding the row
  //     when neither name is present means iPhone users never see
  //     a toggle they can't actually use. The right path for them
  //     is "Add to Home Screen" — once installed as a PWA, the
  //     chrome is gone anyway and the toggle would be redundant.
  //   * Android Chrome / Edge / Firefox: supported (unprefixed).
  //   * iPad Safari: supported (webkit-prefixed on older versions,
  //     unprefixed since 16.4).
  //   * Desktop Safari: supported (webkit-prefixed pre-16.4,
  //     unprefixed since 16.4).
  //   * Desktop Chrome / Firefox / Edge: supported (unprefixed).
  //
  // The toggle is purely a runtime mirror of document.fullscreenElement
  // — we deliberately do NOT persist a "user wants fullscreen" pref to
  // localStorage. Browsers reject programmatic requestFullscreen
  // outside a user gesture, so an "auto-restore on next launch" path
  // wouldn't work anyway, and pretending it might is worse than
  // requiring the parent to flip the toggle once per session.
  const fsHtml = typeof document !== 'undefined' ? document.documentElement : null;
  const fullscreenSupported =
    typeof document !== 'undefined' &&
    (document.fullscreenEnabled === true ||
      document.webkitFullscreenEnabled === true ||
      typeof fsHtml?.requestFullscreen === 'function' ||
      typeof fsHtml?.webkitRequestFullscreen === 'function');

  /** True if the runtime exposed only the webkit-prefixed names.
   *  Used to pick the right addEventListener / removeEventListener
   *  event names below. (When both are available — modern Safari —
   *  the unprefixed event is the one that fires, so listening for
   *  both is harmless and is the safest choice.) */
  const fullscreenHasUnprefixed =
    typeof document !== 'undefined' &&
    typeof fsHtml?.requestFullscreen === 'function';
  const fullscreenHasWebkit =
    typeof document !== 'undefined' &&
    typeof fsHtml?.webkitRequestFullscreen === 'function';

  /** Read the current fullscreen element through whichever name the
   *  runtime exposes. Older Safari only sets webkitFullscreenElement;
   *  modern browsers set both. */
  function currentFullscreenElement() {
    if (typeof document === 'undefined') return null;
    return document.fullscreenElement ?? document.webkitFullscreenElement ?? null;
  }

  /** @type {HTMLLabelElement | null} */
  let fullscreenRow = null;
  /** @type {HTMLInputElement | null} */
  let fullscreenToggle = null;
  /** Sync the visible toggle state to the live fullscreen element.
   *  Triggered by the fullscreenchange (or webkitfullscreenchange)
   *  event so the checkbox always reflects reality even when the
   *  user exits via Esc, the OS gesture, or another tab. */
  function syncFullscreenToggle() {
    if (!fullscreenToggle) return;
    fullscreenToggle.checked = currentFullscreenElement() != null;
  }

  // Detect "already in a chromeless app surface" — covers both:
  //   * Web App Manifest standalone mode (Android Chrome / Edge / desktop
  //     PWAs and Safari 16+ launched from the Home Screen).
  //   * Legacy iOS standalone flag set by older Safari when launched
  //     from a Home Screen icon. window.navigator.standalone is the
  //     pre-manifest signal and is still the only thing iPhone Safari
  //     reliably sets, so we check it explicitly.
  // If we're already chrome-free, neither the toggle (which doesn't
  // exist on iPhone Safari anyway) nor the "Add to Home Screen" guide
  // is useful — the parent has already done what we'd be telling them
  // to do, and showing the guide would be confusing.
  const isStandalonePwa =
    typeof window !== 'undefined' &&
    ((typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator?.standalone === true);

  if (fullscreenSupported) {
    fullscreenRow = document.createElement('label');
    fullscreenRow.className = 'settings-row';

    const fullscreenLabel = document.createElement('span');
    fullscreenLabel.className = 'settings-row__label';
    fullscreenLabel.textContent = t('settings.fullScreen');

    fullscreenToggle = document.createElement('input');
    fullscreenToggle.type = 'checkbox';
    fullscreenToggle.className = 'settings-row__toggle';
    fullscreenToggle.checked = currentFullscreenElement() != null;
    fullscreenToggle.addEventListener('change', () => {
      // Both requestFullscreen and exitFullscreen return promises
      // that may reject — most commonly when the browser denies the
      // request because we're outside a user gesture (shouldn't
      // happen here since the change event IS user-initiated) or
      // because of an iframe sandbox restriction. We catch and warn
      // rather than letting the rejection bubble; the toggle's
      // visible state will get re-synced by the next
      // fullscreenchange event (or by the manual sync below if no
      // event ever fires because the request was rejected outright).
      //
      // Webkit-prefixed Safari quirk: webkitRequestFullscreen
      // returns undefined (not a promise) on older versions. The
      // `req && typeof req.then === 'function'` guard handles that
      // case by simply skipping the .catch wiring; the
      // webkitfullscreenchange listener registered below will still
      // sync the toggle if the request succeeds, and if it fails
      // there's nothing to catch anyway since no promise is returned.
      if (fullscreenToggle?.checked) {
        const target = document.documentElement;
        const req =
          target?.requestFullscreen?.() ?? target?.webkitRequestFullscreen?.();
        if (req && typeof req.then === 'function') {
          req.catch((err) => {
            console.warn('[settings-panel] requestFullscreen failed:', err);
            syncFullscreenToggle();
          });
        }
      } else {
        const exit =
          document.exitFullscreen?.() ?? document.webkitExitFullscreen?.();
        if (exit && typeof exit.then === 'function') {
          exit.catch((err) => {
            console.warn('[settings-panel] exitFullscreen failed:', err);
            syncFullscreenToggle();
          });
        }
      }
    });

    fullscreenRow.appendChild(fullscreenLabel);
    fullscreenRow.appendChild(fullscreenToggle);
    body.appendChild(fullscreenRow);

    // Mirror the document fullscreen state into our checkbox so a
    // user pressing Esc / swiping out of fullscreen flips the toggle
    // back off without needing to reopen the panel. Listen for
    // BOTH the unprefixed and the webkit event: modern Safari fires
    // only the unprefixed one, older Safari fires only the prefixed
    // one, and listening to both means we don't need to know which
    // is in use at construction time.
    if (fullscreenHasUnprefixed) {
      document.addEventListener('fullscreenchange', syncFullscreenToggle);
    }
    if (fullscreenHasWebkit) {
      document.addEventListener('webkitfullscreenchange', syncFullscreenToggle);
    }
  } else if (!isStandalonePwa) {
    // No Fullscreen API AND not yet running as a Home Screen PWA —
    // this is iPhone Safari in a normal browser tab. We can't programmatically
    // enter fullscreen here, but the parent CAN get a chrome-free
    // experience by adding the app to the Home Screen and launching
    // it from there (the manifest declares display: standalone, which
    // hides the URL bar and bottom toolbar on launch). Render a short
    // step-by-step guide in place of the toggle so the parent has an
    // actionable path instead of nothing.
    //
    // We use a <section> with an aria-labelledby pointing at the
    // heading so screen readers announce it as a discrete region,
    // matching the About / Support sections below.
    const fsGuide = document.createElement('section');
    fsGuide.className = 'settings-fullscreen-guide';
    fsGuide.setAttribute(
      'aria-labelledby',
      'settings-fullscreen-guide-heading',
    );

    const fsGuideHeading = document.createElement('h3');
    fsGuideHeading.id = 'settings-fullscreen-guide-heading';
    fsGuideHeading.className = 'settings-fullscreen-guide__heading';
    fsGuideHeading.textContent = t('settings.fullScreenIosHeading');
    fsGuide.appendChild(fsGuideHeading);

    const fsGuideBody = document.createElement('p');
    fsGuideBody.className = 'settings-fullscreen-guide__body';
    fsGuideBody.textContent = t('settings.fullScreenIosBody');
    fsGuide.appendChild(fsGuideBody);

    // Ordered list — the steps must be followed in sequence (the
    // Share sheet only appears after step 1, "Add to Home Screen"
    // only after the share sheet is up, etc.). Using <ol> rather than
    // <ul> communicates that ordering to assistive tech.
    const fsGuideSteps = document.createElement('ol');
    fsGuideSteps.className = 'settings-fullscreen-guide__steps';
    for (const stepKey of [
      'settings.fullScreenIosStep1',
      'settings.fullScreenIosStep2',
      'settings.fullScreenIosStep3',
    ]) {
      const li = document.createElement('li');
      li.className = 'settings-fullscreen-guide__step';
      li.textContent = t(stepKey);
      fsGuideSteps.appendChild(li);
    }
    fsGuide.appendChild(fsGuideSteps);

    body.appendChild(fsGuide);
  }

  // --- About section --------------------------------------------------------
  // Grouped into a <section> so screen readers announce it as a
  // discrete region. We use an <h3> heading because <h2> is the
  // panel title; keeping the heading hierarchy clean means assistive
  // tech can skim the panel.
  //
  // Contents (in order):
  //   - App name (big, display-font)
  //   - Copyright line
  //   - Privacy heading + body paragraph ("we collect nothing")
  //
  // None of these strings are hard-coded here — they all come from
  // the i18n table so future locales can translate them alongside
  // the rest of the UI.
  const aboutSection = document.createElement('section');
  aboutSection.className = 'settings-about';
  aboutSection.setAttribute('aria-labelledby', 'settings-about-heading');

  const aboutHeading = document.createElement('h3');
  aboutHeading.id = 'settings-about-heading';
  aboutHeading.className = 'settings-about__heading';
  aboutHeading.textContent = t('settings.about');
  aboutSection.appendChild(aboutHeading);

  const aboutAppName = document.createElement('p');
  aboutAppName.className = 'settings-about__app-name';
  aboutAppName.textContent = t('about.appName');
  aboutSection.appendChild(aboutAppName);

  const aboutCopyright = document.createElement('p');
  aboutCopyright.className = 'settings-about__copyright';
  aboutCopyright.textContent = t('about.copyright');
  aboutSection.appendChild(aboutCopyright);

  // Contact line with mailto link, placed between copyright and privacy.
  const aboutContact = document.createElement('p');
  aboutContact.className = 'settings-about__contact';
  aboutContact.appendChild(document.createTextNode(t('about.contact')));
  const contactLink = document.createElement('a');
  contactLink.className = 'settings-about__contact-link';
  contactLink.href = `mailto:${t('about.contactEmail')}`;
  contactLink.textContent = t('about.contactEmail');
  aboutContact.appendChild(contactLink);
  aboutSection.appendChild(aboutContact);

  const privacyHeading = document.createElement('h4');
  privacyHeading.className = 'settings-about__privacy-heading';
  privacyHeading.textContent = t('about.privacyHeading');
  aboutSection.appendChild(privacyHeading);

  const privacyBody = document.createElement('p');
  privacyBody.className = 'settings-about__privacy-body';
  privacyBody.textContent = t('about.privacyBody');
  aboutSection.appendChild(privacyBody);

  // Analytics disclosure paragraph. Splits the body text + link text
  // + trailing period into three pieces so the anchor is a real
  // clickable <a> rather than an HTML-in-string injection. The
  // anchor opens in a new tab (target=_blank) with
  // noopener/noreferrer so the analytics page can't reach back into
  // the PWA, same hardening we do for the PayPal form.
  const privacyAnalyticsBody = document.createElement('p');
  privacyAnalyticsBody.className = 'settings-about__privacy-analytics';
  privacyAnalyticsBody.appendChild(
    document.createTextNode(t('about.privacyAnalyticsBody')),
  );
  const analyticsLink = document.createElement('a');
  analyticsLink.className = 'settings-about__privacy-analytics-link';
  analyticsLink.href = ANALYTICS_URL;
  analyticsLink.target = '_blank';
  analyticsLink.rel = 'noopener noreferrer';
  analyticsLink.textContent = t('about.privacyAnalyticsLinkText');
  privacyAnalyticsBody.appendChild(analyticsLink);
  privacyAnalyticsBody.appendChild(document.createTextNode('.'));
  aboutSection.appendChild(privacyAnalyticsBody);

  const appVersion = document.createElement('p');
  appVersion.className = 'settings-about__version';
  appVersion.textContent = `v${__APP_VERSION__}`;
  aboutSection.appendChild(appVersion);

  // The About section is appended below, after Support, so that the
  // copyright + privacy notice land at the very bottom of the drawer.

  // --- Support (PayPal donation) section -----------------------------------
  // The support section wraps PayPal's "no-code payment" form —
  // a plain HTML form POST that opens paypal.com in a new tab.
  // No SDK, no tracking script, no wallet detection. All styling
  // lives in settings.css; here we just build the DOM.
  //
  // Offline handling: the form posts to an external URL and loads
  // brand images from paypalobjects.com. Both of those fail offline,
  // which is the expected state for this app (kid in the car, no
  // signal). When offline, we hide the form via a data attribute
  // and surface a short "connect to the internet" hint instead.
  // We react to online/offline events so that if the parent opens
  // the drawer while offline and then the device reconnects, the
  // form appears without needing to reopen the drawer.
  const supportSection = document.createElement('section');
  supportSection.className = 'settings-support';
  supportSection.setAttribute('aria-labelledby', 'settings-support-heading');
  supportSection.dataset.online = navigator.onLine ? 'true' : 'false';

  const supportHeading = document.createElement('h3');
  supportHeading.id = 'settings-support-heading';
  supportHeading.className = 'settings-support__heading';
  supportHeading.textContent = t('support.heading');
  supportSection.appendChild(supportHeading);

  const supportBody = document.createElement('p');
  supportBody.className = 'settings-support__body';
  supportBody.textContent = t('support.body');
  supportSection.appendChild(supportBody);

  // --- The actual PayPal form ---
  // Structure mirrors the snippet PayPal's no-code dashboard hands
  // out: a <form> wrapping a submit <input>, a card-brand icon
  // row, and a "Powered by PayPal" wordmark. We use target="_blank"
  // so the donation flow opens in a new tab and the kids' app
  // stays where it is; `rel="noopener noreferrer"` prevents the
  // new tab from having window.opener access back into our PWA.
  const supportForm = document.createElement('form');
  supportForm.className = 'settings-support__form';
  supportForm.action = PAYPAL_FORM_ACTION;
  supportForm.method = 'post';
  supportForm.target = '_blank';
  // Use setAttribute rather than the .rel property: HTMLFormElement.rel
  // is a relatively recent addition to the DOM spec and older DOM
  // implementations (including jsdom at the version we test against)
  // don't reflect it back to the attribute, which means a
  // querySelector check would see rel=null. setAttribute is
  // universally honored.
  supportForm.setAttribute('rel', 'noopener noreferrer');

  const supportSubmit = document.createElement('input');
  supportSubmit.type = 'submit';
  // Preserve PayPal's own class name alongside our class so that
  // if their backend ever adds verification hooks that grep for
  // "pp-XX668BWXUM9AY" on the DOM, we don't break.
  supportSubmit.className = 'settings-support__button pp-XX668BWXUM9AY';
  supportSubmit.value = t('support.buttonLabel');
  supportForm.appendChild(supportSubmit);

  const supportCards = document.createElement('img');
  supportCards.className = 'settings-support__cards';
  supportCards.src = PAYPAL_CARDS_IMG;
  supportCards.alt = 'cards';
  // Explicit loading=lazy so this image doesn't block first paint
  // of the drawer when the parent first opens settings.
  supportCards.loading = 'lazy';
  supportForm.appendChild(supportCards);

  const poweredByWrap = document.createElement('section');
  poweredByWrap.className = 'settings-support__powered-by';
  // Text node + wordmark img. The wordmark is a PayPal brand asset
  // and must be shown verbatim (per PayPal brand guidelines) when
  // their payment link is embedded.
  poweredByWrap.appendChild(document.createTextNode(t('support.poweredBy') + ' '));
  const wordmark = document.createElement('img');
  wordmark.className = 'settings-support__wordmark';
  wordmark.src = PAYPAL_WORDMARK_IMG;
  wordmark.alt = 'PayPal';
  wordmark.loading = 'lazy';
  poweredByWrap.appendChild(wordmark);
  supportForm.appendChild(poweredByWrap);

  supportSection.appendChild(supportForm);

  // --- Offline hint (shown in place of the form when offline) ---
  const offlineHint = document.createElement('p');
  offlineHint.className = 'settings-support__offline-hint';
  offlineHint.textContent = t('support.offlineHint');
  supportSection.appendChild(offlineHint);

  // Live online/offline toggle — flip the data attribute and let
  // CSS do the show/hide work. Listeners are cleaned up in destroy().
  function handleOnline() {
    supportSection.dataset.online = 'true';
  }
  function handleOffline() {
    supportSection.dataset.online = 'false';
  }
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  body.appendChild(supportSection);

  // --- "More Ewe Apps" family cross-promo section --------------------------
  // Sits between Support and About. A small list of sibling Ewe apps
  // with icon + tagline tiles. Opens each link in a new tab (same
  // hardening as the PayPal form and the analytics link).
  //
  // Offline handling mirrors the Support section: when navigator.onLine
  // flips to false, we hide the tile list and show a short hint in its
  // place. The tap would otherwise open a new tab to a domain we can't
  // reach — the link itself doesn't fail silently, it fails with a
  // browser error page, which is a worse experience than "you're
  // offline right now." Same online/offline listeners drive the flip.
  //
  // The list of apps comes from family-apps.js with self filtered out,
  // so this app never links to itself. When a new app ships, the only
  // change needed is in family-apps.js — this renderer walks the list.
  const familySection = document.createElement('section');
  familySection.className = 'settings-family';
  familySection.setAttribute('aria-labelledby', 'settings-family-heading');
  familySection.dataset.online = navigator.onLine ? 'true' : 'false';

  const familyHeading = document.createElement('h3');
  familyHeading.id = 'settings-family-heading';
  familyHeading.className = 'settings-family__heading';
  familyHeading.textContent = t('family.heading');
  familySection.appendChild(familyHeading);

  const familyBody = document.createElement('p');
  familyBody.className = 'settings-family__body';
  familyBody.textContent = t('family.body');
  familySection.appendChild(familyBody);

  const familyList = document.createElement('ul');
  familyList.className = 'settings-family__list';
  for (const app of getOtherFamilyApps()) {
    const li = document.createElement('li');
    li.className = 'settings-family__item';

    const tile = document.createElement('a');
    tile.className = 'settings-family__tile';
    tile.href = app.url;
    tile.target = '_blank';
    // Same hardening as the PayPal form + analytics link: the new tab
    // must not have window.opener access back into the PWA.
    tile.rel = 'noopener noreferrer';
    // data-app-id makes it straightforward for tests to find the tile
    // for a specific family member without relying on text matches.
    tile.dataset.appId = app.id;

    const icon = document.createElement('img');
    icon.className = 'settings-family__icon';
    icon.src = app.icon;
    icon.alt = '';
    // The icon is decorative — the accessible name comes from the app
    // name text below. width/height are hinted for layout; CSS sizes
    // the rendered tile.
    icon.width = 64;
    icon.height = 64;
    icon.loading = 'lazy';
    tile.appendChild(icon);

    const textWrap = document.createElement('span');
    textWrap.className = 'settings-family__text';

    const nameEl = document.createElement('span');
    nameEl.className = 'settings-family__name';
    nameEl.textContent = app.name;
    textWrap.appendChild(nameEl);

    const taglineEl = document.createElement('span');
    taglineEl.className = 'settings-family__tagline';
    taglineEl.textContent = t(app.taglineKey);
    textWrap.appendChild(taglineEl);

    tile.appendChild(textWrap);
    li.appendChild(tile);
    familyList.appendChild(li);
  }
  familySection.appendChild(familyList);

  const familyOfflineHint = document.createElement('p');
  familyOfflineHint.className = 'settings-family__offline-hint';
  familyOfflineHint.textContent = t('family.offlineHint');
  familySection.appendChild(familyOfflineHint);

  // Dedicated online/offline listeners for the family section. We
  // deliberately register separate listeners rather than overloading
  // the Support section's handlers — the Support section's handlers
  // are referenced by destroy() and mixing responsibilities would
  // couple two otherwise-independent features. The cleanup is paired
  // up in destroy() below.
  function handleFamilyOnline() {
    familySection.dataset.online = 'true';
  }
  function handleFamilyOffline() {
    familySection.dataset.online = 'false';
  }
  window.addEventListener('online', handleFamilyOnline);
  window.addEventListener('offline', handleFamilyOffline);

  body.appendChild(familySection);
  body.appendChild(aboutSection);

  // --- Open / close state ---------------------------------------------------
  let open = false;

  /**
   * Reflect the panel's open state on `<html>` via a data attribute
   * so global stylesheets can react. Currently used by grid.css to
   * dim and disable the sound buttons while the drawer is up — the
   * child shouldn't be able to hammer animal sounds out the back of
   * an open settings drawer. Guarded against missing
   * documentElement so the module is safe to import in non-browser
   * environments (the same guard pattern animation-manager uses).
   */
  function setSettingsOpenAttr(isOpen) {
    if (typeof document === 'undefined' || !document.documentElement) return;
    if (isOpen) {
      document.documentElement.setAttribute('data-settings-open', '');
    } else {
      document.documentElement.removeAttribute('data-settings-open');
    }
  }

  function setOpen(next) {
    if (next === open) return;
    open = next;
    panelElement.hidden = !open;
    panelElement.classList.toggle('settings-panel--open', open);
    toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    setSettingsOpenAttr(open);
    if (open) {
      // Reflect current settings each time we open (in case the
      // underlying values changed via another code path).
      verbalToggle.checked = getVerbalDescriptions();
      langSelect.value = getLocale();
      closeBtn.focus();
    } else {
      // Closing always wipes gate state so the next open cycle
      // starts fresh at the home corner in the neutral color —
      // otherwise closing a just-opened panel would leave the
      // gear sitting in the teleported "ready" position.
      resetGate();
      toggleButton.focus();
    }
  }

  function openPanel() {
    setOpen(true);
  }
  function closePanel() {
    setOpen(false);
  }
  function isOpen() {
    return open;
  }

  // --- Child-proof gate (Phase 7) ------------------------------------------
  // Accidental settings-panel opens are the single biggest annoyance
  // for parents handing an iPad to a toddler: the kid taps the gear,
  // the drawer slides in, the kid is suddenly looking at a UI they
  // can't make sense of, and the parent has to intervene. Our gate
  // is a three-tap state machine:
  //
  //   State 0 (neutral, gray)    ─ tap ─▶  State 1 (primed, amber)
  //   State 1 (primed, amber)    ─ tap ─▶  State 2 (ready, green)
  //                                        + teleport to opposite corner
  //   State 2 (ready, green)     ─ tap ─▶  open panel, reset to State 0
  //
  // If the parent stops tapping for GATE_IDLE_RESET_MS (4s) at any
  // point, the gate resets — so an accidental single tap by the
  // child does NOT linger as half-progress that the parent could
  // complete minutes later with one more tap.
  //
  // Threat model: a 1-to-4-year-old. A toddler will not watch a
  // button teleport across the screen and reorient to tap it in the
  // new corner; they'll tap the empty space where it used to be.
  // Parents will figure out the pattern in a few seconds.
  //
  // This gate applies ONLY to taps on the toggle button. The
  // imperative `panel.open()` method intentionally bypasses it, so
  // tests and future deep-link handlers don't have to simulate the
  // three-tap sequence.
  const GATE_IDLE_RESET_MS = 4000;
  let gateTapCount = 0;
  let gateResetTimer = null;

  function clearGateClasses() {
    toggleButton.classList.remove(
      'settings-toggle--primed',
      'settings-toggle--ready',
      'settings-toggle--teleported',
    );
  }

  function resetGate() {
    if (gateResetTimer !== null) {
      clearTimeout(gateResetTimer);
      gateResetTimer = null;
    }
    gateTapCount = 0;
    clearGateClasses();
  }

  function scheduleGateReset() {
    if (gateResetTimer !== null) {
      clearTimeout(gateResetTimer);
    }
    gateResetTimer = setTimeout(resetGate, GATE_IDLE_RESET_MS);
  }

  function advanceGate() {
    gateTapCount += 1;
    if (gateTapCount === 1) {
      // State 1 — primed. Subtle visual bump, no position change yet.
      clearGateClasses();
      toggleButton.classList.add('settings-toggle--primed');
      scheduleGateReset();
    } else if (gateTapCount === 2) {
      // State 2 — ready. Color advances AND the gear teleports to
      // the diagonally-opposite corner so the final tap requires
      // deliberate re-aiming (which a toddler won't do).
      clearGateClasses();
      toggleButton.classList.add(
        'settings-toggle--ready',
        'settings-toggle--teleported',
      );
      scheduleGateReset();
    } else {
      // State 3+ — gate cleared, actually open the drawer. Reset
      // the gate BEFORE opening so the gear goes back to neutral
      // home-corner the moment the panel appears; that way when
      // the parent closes the panel the gear is already where
      // they expect it to be for next time.
      resetGate();
      setOpen(true);
    }
  }

  toggleButton.addEventListener('click', () => {
    // If the panel is already open, a tap on the gear is "close the
    // drawer". No gate applies on the close path.
    if (open) {
      setOpen(false);
      return;
    }
    advanceGate();
  });
  closeBtn.addEventListener('click', () => {
    setOpen(false);
  });

  // --- Outside-tap gate reset ---------------------------------------------
  // Once the gate has started advancing (state 1 or state 2) but
  // before the panel actually opens, ANY tap that lands somewhere
  // other than the gear itself should snap the gate back to neutral
  // (state 0, gray, home corner). This is the second half of the
  // child-proofing story: the time-based reset above handles the
  // "kid taps once and walks away" case, and this handler handles
  // the "kid taps the gear, then taps an animal button to play a
  // sound, then taps the gear again" case — without it, the kid's
  // intervening animal-button tap leaves the gate primed and the
  // next gear tap would advance into ready / open territory.
  //
  // Listener is on the document in the capture phase so it sees the
  // event before the target's own click handler runs (the order
  // doesn't really matter for state correctness — both end up
  // running, and resetGate() is idempotent — but capture is the
  // less-surprising spot for a global "any tap" listener).
  //
  // We listen on `click` rather than `pointerdown` because:
  //   1. JSDOM-driven tests use button.click() which fires a click
  //      event but no pointerdown, so a pointerdown listener would
  //      be invisible to the suite.
  //   2. iOS Safari delivers a click for every tap (touch + mouse
  //      + assistive switch input), so it's the universal "the user
  //      tapped something" signal that doesn't depend on the
  //      pointer-events stack.
  function handleDocumentClick(event) {
    if (open) return; // gate semantics don't apply once the drawer is up
    if (gateTapCount === 0) return; // nothing primed, nothing to reset
    // event.target may be null in synthetic events; defensive check.
    const target = event.target;
    if (target && toggleButton.contains(target)) return; // tap on gear itself
    resetGate();
  }
  document.addEventListener('click', handleDocumentClick, true);

  // Close on Escape when the panel is open.
  function handleKey(event) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
    }
  }
  document.addEventListener('keydown', handleKey);

  function destroy() {
    // Mark as closed first so isOpen() reflects the torn-down state
    // and the document-level keydown listener is a no-op even if
    // it somehow fires between removeEventListener and GC.
    open = false;
    // Clear the data-settings-open attribute we may have set on
    // <html> so a torn-down panel doesn't leave global stylesheets
    // stuck in the "settings open" visual state for the next panel
    // (or for the rest of the page).
    setSettingsOpenAttr(false);
    // Kill any pending gate timer so it doesn't fire after teardown
    // and touch a detached DOM node.
    if (gateResetTimer !== null) {
      clearTimeout(gateResetTimer);
      gateResetTimer = null;
    }
    document.removeEventListener('keydown', handleKey);
    // Outside-tap gate-reset listener was registered in capture
    // phase, so the removeEventListener call must pass the same
    // `true` capture flag — otherwise removeEventListener silently
    // fails to find the listener and leaks it.
    document.removeEventListener('click', handleDocumentClick, true);
    // The fullscreenchange listener is only registered when the
    // Fullscreen API is supported (see fullscreenSupported above),
    // so its removal is gated on the same flag. Without this guard
    // we'd remove a listener we never registered (harmless) on
    // platforms like iPhone Safari where the row was hidden.
    if (fullscreenSupported) {
      document.removeEventListener('fullscreenchange', syncFullscreenToggle);
    }
    // Remove the window-level connectivity listeners we registered
    // for the Support section. These live on the window (not the
    // panel element) so they survive panel removal unless we
    // explicitly unhook them here — missing this leaks a handler
    // every time a panel is created and destroyed.
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    // Same cleanup for the Family section's independent listeners.
    window.removeEventListener('online', handleFamilyOnline);
    window.removeEventListener('offline', handleFamilyOffline);
    panelElement.remove();
    toggleButton.remove();
  }

  return {
    toggleButton,
    panelElement,
    open: openPanel,
    close: closePanel,
    isOpen,
    destroy,
  };
}
